import { useEffect } from "react";
import { Link, useNavigate, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Logo } from "../../components/Logo";
import { Reveal } from "../../components/Reveal";
import { useLang } from "../../lib/i18n";
import { SERVICES, SERVICE_ORDER, SERVICE_IMAGES } from "./data";

const Eyebrow = ({ children }) => (
  <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-faint">
    <span className="h-1.5 w-1.5 rounded-full bg-amber" aria-hidden="true" />
    {children}
  </p>
);

export default function ServicePage() {
  const { slug } = useParams();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const service = SERVICES[slug];

  const c = service ? service[lang] : null;

  useEffect(() => {
    if (!c) return;
    const meta = document.querySelector('meta[name="description"]');
    const prevTitle = document.title;
    const prevDesc = meta?.getAttribute("content");
    document.title = c.meta.title;
    if (meta) meta.setAttribute("content", c.meta.description);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [slug, lang]);

  if (!service) return <Navigate to="/diensten" replace />;

  const nextSlug = SERVICE_ORDER[(SERVICE_ORDER.indexOf(slug) + 1) % SERVICE_ORDER.length];
  const nextTitle = SERVICES[nextSlug][lang].title;
  const img = SERVICE_IMAGES[slug];

  return (
    <div className="min-h-screen bg-ink" data-testid="service-page">
      <header className="border-b border-white/10 bg-[#222222]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 rounded-full border border-white/15 bg-white/5 p-1" data-testid="service-lang-toggle">
              {["nl", "en"].map((code) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  data-testid={`service-lang-${code}`}
                  className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    lang === code ? "bg-amber font-bold text-[#181510]" : "text-white/50 hover:text-white"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
            <Link to="/diensten" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber" data-testid="service-back-link">
              <ArrowLeft size={13} />
              {lang === "nl" ? "Alle diensten" : "All services"}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="mx-auto max-w-6xl px-6 pb-16 pt-14 md:pt-20" data-testid="service-hero">
          <Reveal>
            <Eyebrow>Dienst {c.num} · 4thscenery</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-archivo text-4xl font-black leading-[1.05] tracking-tight text-cream sm:text-5xl" data-testid="service-title">
              {c.title}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-cream/85">{c.tagline}</p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-tan">{c.intro}</p>
            <div className="mt-7 flex flex-wrap gap-2" data-testid="service-audiences">
              {c.audiences.map((a, i) => (
                <span key={i} className="rounded-full border border-edge/70 bg-white px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/80" data-testid={`service-audience-${i}`}>
                  {a}
                </span>
              ))}
            </div>
            <button
              onClick={() => navigate("/", { state: { scrollTo: "#gesprek" } })}
              className="group mt-9 inline-flex items-center gap-2 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-sm font-bold text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
              data-testid="service-cta-button"
            >
              {lang === "nl" ? "Plan een gratis kennismaking" : "Book a free intro call"}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </Reveal>
          {img && (
            <Reveal delay={0.15}>
              <div className="mt-12 overflow-hidden rounded-[16px] border border-edge/40 shadow-xl shadow-black/10" data-testid="service-image-wrap">
                <img
                  src={img.src}
                  alt={lang === "nl" ? img.altNl : img.altEn}
                  className="h-56 w-full object-cover transition-transform duration-700 hover:scale-[1.03] sm:h-72 md:h-96"
                  loading="lazy"
                  data-testid="service-image"
                />
              </div>
            </Reveal>
          )}
        </section>

        <section className="border-y border-edge/40 bg-[#F7F4EC]" data-testid="service-problem">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <Reveal>
              <Eyebrow>{lang === "nl" ? "Het probleem" : "The problem"}</Eyebrow>
              <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.problemTitle}</h2>
              <p className="mt-4 max-w-2xl leading-relaxed text-tan">{c.problemText}</p>
              <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                {c.problemPoints.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 rounded-[10px] border border-edge/50 bg-white px-4 py-3.5 text-sm text-cream">
                    <span className="h-2 w-2 rotate-45 bg-amber" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="service-steps">
          <Reveal>
            <Eyebrow>{lang === "nl" ? "De werkwijze" : "The process"}</Eyebrow>
            <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.stepsTitle}</h2>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              {c.steps.map((s, i) => (
                <div key={i} className="rounded-[12px] border border-edge/60 bg-white p-6 transition-colors duration-300 hover:border-amber/60" data-testid={`service-step-${i}`}>
                  <p className="font-mono text-[11px] font-bold tracking-[0.25em] text-amberdeep">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-2 font-archivo text-lg font-extrabold tracking-tight text-cream">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-tan">{s.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="border-y border-edge/40 bg-[#F7F4EC]" data-testid="service-deliverables">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <Reveal>
              <Eyebrow>{lang === "nl" ? "De oplevering" : "Deliverables"}</Eyebrow>
              <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.deliverablesTitle}</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                {c.deliverables.map((d, i) => (
                  <div key={i} className="flex gap-4 rounded-[12px] border border-edge/60 bg-white p-6" data-testid={`service-deliverable-${i}`}>
                    <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber">
                      <Check size={14} strokeWidth={3} className="text-[#181510]" />
                    </span>
                    <div>
                      <h3 className="font-archivo text-base font-extrabold tracking-tight text-cream">{d.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-tan">{d.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="service-why">
          <Reveal>
            <Eyebrow>{lang === "nl" ? "Waarom wij" : "Why us"}</Eyebrow>
            <h2 className="mt-5 font-archivo text-3xl font-black tracking-tight text-cream">{c.whyTitle}</h2>
            <ul className="mt-7 max-w-2xl space-y-3.5">
              {c.whyPoints.map((p, i) => (
                <li key={i} className="flex items-start gap-3 leading-relaxed text-cream/90">
                  <span className="mt-2 h-2 w-2 shrink-0 rotate-45 bg-amber" aria-hidden="true" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </section>

        <section className="mx-auto max-w-6xl px-6 pb-20" data-testid="service-cta">
          <Reveal>
            <div className="rounded-[16px] bg-[#181510] p-8 md:p-12">
              <h2 className="max-w-xl font-archivo text-3xl font-black tracking-tight text-white">{c.ctaTitle}</h2>
              <p className="mt-3 max-w-xl leading-relaxed text-white/70">{c.ctaText}</p>
              <div className="mt-7 flex flex-wrap items-center gap-5">
                <button
                  onClick={() => navigate("/", { state: { scrollTo: "#gesprek" } })}
                  className="group inline-flex items-center gap-2 rounded-[10px] bg-amber px-7 py-3.5 font-archivo text-sm font-bold text-[#181510] transition-colors duration-300 hover:bg-amberdeep"
                  data-testid="service-cta-button-bottom"
                >
                  {lang === "nl" ? "Plan een gratis kennismaking" : "Book a free intro call"}
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
                <Link
                  to={`/diensten/${nextSlug}`}
                  className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-amber"
                  data-testid="service-next-link"
                >
                  {lang === "nl" ? "Volgende dienst" : "Next service"}: {nextTitle}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-edge/40 py-8">
        <p className="text-center font-mono text-[11px] uppercase tracking-[0.2em] text-faint" data-testid="service-footer">
          © 2026 4thscenery · Beyond The Ordinary
        </p>
      </footer>
    </div>
  );
}
